import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["currency", "disabled", "disabledMaxButtonTooltip", "disabledMaxButtonTooltipProps", "disabledMinButtonTooltip", "disabledMinButtonTooltipProps", "max", "min", "onStep", "placeholderValue", "readOnly", "stepSize", "value", "cssStyles", "parser"];
import { Component } from 'react';
import ShareInput from '../ShareInput/ShareInput';
import { Controllable } from '../Controllable/Controllable';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { callIfPossible } from '../../utils/functions';
import { isLeftClick } from '../../utils/isLeftClick';
import { repeatUntilEventOnElement } from '../../utils/DomEvents';
import { CurrencyInput } from '../CurrencyInput';
import { NumberInput } from '../NumberInput';
import { ButtonGroup } from '../ButtonGroup';
import { Tooltip } from '../Tooltip';
import { Button } from '../Button';
import { DownIcon, UpIcon } from 'foundations-assets';
import { StyledButton, StyledButtonTextContainer } from '../Button/PrivateButton';
import { StyledInput } from '../TextInput/TextInput';
import { StyledButtonGroup } from '../ButtonGroup/ButtonGroup';
import styled from 'foundations-theming/utils';
import { withTokenProps } from '../../../utils/withTokenProps';
import { VENUS_LAYER } from '../../constants';
import { filterTokenProps } from '../../../utils/filterTokenProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getBaseValue = (value, placeholderValue) => value != null ? value : placeholderValue;
const isIncrementDisabled = (stepSize, baseValue, max, disabled) => {
  if (disabled) return true;
  if (max == null) return false;
  return !(baseValue + stepSize <= max);
};
const isDecrementDisabled = (stepSize, baseValue, min, disabled) => {
  if (disabled) return true;
  if (min == null) return false;
  return !(baseValue - stepSize >= min);
};
const StepperInputWrapper = styled.div.withConfig({
  displayName: "StepperInput__StepperInputWrapper",
  componentId: "sc-7wei72-0"
})(({
  theme,
  $disabled,
  $decrementDisabled,
  $incrementDisabled,
  $cssStyles
}) => {
  const buttonCommonStyles = {
    display: 'block',
    padding: theme.space['0'],
    blockSize: '100%',
    borderTopLeftRadius: theme.borderRadius['0'],
    borderBottomLeftRadius: theme.borderRadius['0'],
    cursor: 'pointer'
  };

  // TODO should this be controlled by a token?
  const stepperControlWidth = 30;
  return Object.assign({
    display: 'flex',
    [`& ${StyledInput}`]: {
      paddingRight: theme.space['1000'],
      '&:focus': {
        [`& + ${StyledButtonGroup}`]: {
          zIndex: VENUS_LAYER
        }
      }
    },
    [`& ${StyledButtonGroup}`]: {
      display: 'inline-flex',
      flexDirection: 'column',
      inlineSize: `${stepperControlWidth}px`,
      marginLeft: `-${stepperControlWidth}px`,
      minWidth: `${stepperControlWidth}px`
    },
    [`& ${StyledButtonTextContainer}`]: {
      justifyContent: 'center',
      display: 'flex',
      alignItems: 'center'
    },
    [`& ${StyledButton}`]: Object.assign({}, buttonCommonStyles, $disabled && {
      cursor: 'not-allowed'
    }, {
      '&:last-of-type': Object.assign({
        marginTop: -theme.space['25'],
        marginBottom: theme.space['25'],
        borderBottom: theme.borderWidth['0'],
        borderTop: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
        borderRight: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
        borderLeft: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`
      }, $decrementDisabled && {
        cursor: 'not-allowed'
      }),
      '&:first-of-type': Object.assign({
        marginTop: theme.space['25'],
        borderTop: theme.borderWidth['0'],
        borderBottom: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
        borderRight: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
        borderLeft: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`
      }, $incrementDisabled && {
        cursor: 'not-allowed'
      })
    })
  }, $cssStyles);
});
class StepperInputComponent extends Component {
  constructor(...args) {
    super(...args);
    this._freeze = false;
    this._repeatHandle = null;
    this.handleStep = step => {
      const {
        disabled,
        inputRef,
        max,
        min,
        onChange,
        onStep,
        placeholderValue = 0,
        readOnly,
        value
      } = this.props;
      if (readOnly || disabled) return;
      if (inputRef.current) inputRef.current.focus();
      const newValue = getBaseValue(value, placeholderValue) + step;
      const fencedMin = min == null ? newValue : Math.max(newValue, min);
      const fencedValue = max == null ? fencedMin : Math.min(fencedMin, max);

      // "15 digits of precision ought to be enough for anyone" --Bill Gates
      const roundedValue = +fencedValue.toPrecision(15);
      onChange(syntheticEvent(roundedValue));
      callIfPossible(onStep, syntheticEvent(roundedValue));
    };
    this.increment = () => {
      if (this._freeze) return;
      const {
        stepSize = 1
      } = this.props;
      this.handleStep(stepSize);
    };
    this.decrement = () => {
      if (this._freeze) return;
      const {
        stepSize = 1
      } = this.props;
      this.handleStep(-stepSize);
    };
    this.handleUpArrowMouseDown = evt => {
      if (isLeftClick(evt)) {
        this.startMouseHold('increment');
      }
    };
    this.handleDownArrowMouseDown = evt => {
      if (isLeftClick(evt)) {
        this.startMouseHold('decrement');
      }
    };
    this.handleArrowMouseUp = () => {
      // Prevent click event from triggering an extra increment/decrement after click-and-hold
      if (this._repeatHandle && this._repeatHandle.count >= 1) {
        this._freeze = true;
        setTimeout(() => {
          this._freeze = false;
        }, 0);
      }
      this._repeatHandle = null;
    };
    this.handleKeyDown = evt => {
      const {
        onKeyDown
      } = this.props;
      const {
        key
      } = evt;
      if (key === 'ArrowUp') {
        evt.preventDefault();
        this.increment();
      } else if (key === 'ArrowDown') {
        evt.preventDefault();
        this.decrement();
      }
      callIfPossible(onKeyDown, evt);
    };
  }
  componentWillUnmount() {
    if (this._repeatHandle != null) {
      this._repeatHandle.stop();
    }
  }
  startMouseHold(action) {
    this._repeatHandle = repeatUntilEventOnElement(this[action], 100, document.documentElement, ['mouseup']);
  }
  render() {
    const _this$props = this.props,
      {
        currency,
        disabled = false,
        disabledMaxButtonTooltip,
        disabledMaxButtonTooltipProps,
        disabledMinButtonTooltip,
        disabledMinButtonTooltipProps,
        max,
        min,
        placeholderValue = 0,
        readOnly,
        stepSize = 1,
        value,
        cssStyles,
        parser
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const baseValue = getBaseValue(value, placeholderValue);
    const incrementDisabled = isIncrementDisabled(stepSize, baseValue, max, disabled);
    const decrementDisabled = isDecrementDisabled(stepSize, baseValue, min, disabled);
    const Input = currency ? CurrencyInput : NumberInput;

    // filter out token props from rest to prevent passing them to both the input and the wrapper
    const {
      tokenProps: __tokenProps,
      restProps: restWithoutTokenProps
    } = filterTokenProps(rest);
    return /*#__PURE__*/_jsxs(StepperInputWrapper, {
      $disabled: Boolean(disabled),
      $incrementDisabled: incrementDisabled,
      $decrementDisabled: decrementDisabled,
      $cssStyles: cssStyles,
      children: [/*#__PURE__*/_jsx(Input, Object.assign({}, restWithoutTokenProps, {
        "aria-valuemax": max,
        "aria-valuemin": min,
        "aria-valuenow": value !== null && value !== void 0 ? value : undefined,
        currency: currency !== null && currency !== void 0 ? currency : '',
        disabled: disabled,
        max: max,
        min: min,
        onKeyDown: this.handleKeyDown,
        readOnly: readOnly,
        role: "spinbutton",
        value: value !== null && value !== void 0 ? value : undefined,
        parser: parser
      })), readOnly ? null : /*#__PURE__*/_jsxs(ButtonGroup, {
        orientation: "vertical",
        children: [/*#__PURE__*/_jsx(Tooltip, Object.assign({
          placement: "top left"
        }, disabledMaxButtonTooltipProps, {
          title: disabledMaxButtonTooltip,
          disabled: !incrementDisabled,
          children: /*#__PURE__*/_jsx(Button, {
            "aria-hidden": true,
            disabled: incrementDisabled,
            onClick: this.increment,
            onMouseDown: this.handleUpArrowMouseDown,
            onMouseUp: this.handleArrowMouseUp,
            "data-action": "increment",
            use: "tertiary-light",
            size: "sm",
            tabIndex: -1,
            children: /*#__PURE__*/_jsx(UpIcon, {
              size: "100",
              color: incrementDisabled ? 'text-core-disabled' : 'temp-do-not-use-fill-secondary-default'
            })
          })
        })), /*#__PURE__*/_jsx(Tooltip, Object.assign({
          placement: "top left"
        }, disabledMinButtonTooltipProps, {
          title: disabledMinButtonTooltip,
          disabled: !decrementDisabled,
          children: /*#__PURE__*/_jsx(Button, {
            "aria-hidden": true,
            disabled: decrementDisabled,
            onClick: this.decrement,
            onMouseDown: this.handleDownArrowMouseDown,
            onMouseUp: this.handleArrowMouseUp,
            "data-action": "decrement",
            use: "tertiary-light",
            tabIndex: -1,
            size: "sm",
            children: /*#__PURE__*/_jsx(DownIcon, {
              size: "100",
              color: decrementDisabled ? 'text-core-disabled' : 'temp-do-not-use-fill-secondary-default'
            })
          })
        }))]
      })]
    });
  }
}
StepperInputComponent.displayName = 'StepperInput';
StepperInputComponent.defaultProps = {
  placeholderValue: 0,
  stepSize: 1
};
export const StepperInput = ShareInput(Controllable(withTokenProps(StepperInputComponent)));